import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { 
  TrendingUp, 
  TrendingDown,
  Eye,
  FileBarChart,
  ArrowUpRight,
  ArrowDownRight,
  Building
} from "lucide-react";

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

function formatPercent(value) {
  return `${value.toFixed(1)}%`;
}

export default function DREContratoPage() {
  const [loading, setLoading] = useState(true);
  const [contratos, setContratos] = useState([]);
  const [selectedContrato, setSelectedContrato] = useState(null);
  const [dreData, setDreData] = useState(null);

  useEffect(() => {
    fetchContratos();
  }, []);

  const fetchContratos = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/financeiro/dre-contratos`);
      setContratos(response.data);
    } catch (error) {
      toast.error("Erro ao carregar contratos");
    } finally {
      setLoading(false);
    }
  };

  const fetchDREContrato = async (contratoId) => {
    try {
      const response = await axios.get(`${API}/financeiro/dre-contrato/${contratoId}`);
      setDreData(response.data);
      setSelectedContrato(contratoId);
    } catch (error) {
      toast.error("Erro ao carregar DRE do contrato");
    }
  };

  // Calculate totals
  const totais = contratos.reduce((acc, c) => ({
    valor_contrato: acc.valor_contrato + (c.valor_contrato || 0),
    receitas: acc.receitas + c.receitas,
    despesas: acc.despesas + c.despesas,
    resultado: acc.resultado + c.resultado
  }), { valor_contrato: 0, receitas: 0, despesas: 0, resultado: 0 });

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-luvcorp-navy"></div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="dre-contrato-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold text-luvcorp-navy">DRE por Contrato</h1>
            <p className="text-slate-500">Demonstração do Resultado por Projeto/Contrato</p>
          </div>
        </div>

        {/* Totals Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Valor Total Contratos</p>
              <p className="text-xl font-bold text-luvcorp-navy">{formatCurrency(totais.valor_contrato)}</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Total Receitas</p>
              <p className="text-xl font-bold text-green-600">{formatCurrency(totais.receitas)}</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Total Despesas</p>
              <p className="text-xl font-bold text-red-600">{formatCurrency(totais.despesas)}</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Resultado Geral</p>
              <p className={`text-xl font-bold ${totais.resultado >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(totais.resultado)}
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Contracts Table */}
          <Card className="border-0 shadow-sm lg:col-span-2">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Building className="h-5 w-5 text-luvcorp-gold" />
                Contratos/Projetos
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Contrato</TableHead>
                    <TableHead className="text-right">Valor</TableHead>
                    <TableHead className="text-right">Receitas</TableHead>
                    <TableHead className="text-right">Despesas</TableHead>
                    <TableHead className="text-right">Resultado</TableHead>
                    <TableHead className="text-right">Margem</TableHead>
                    <TableHead className="w-[60px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contratos.map((contrato) => (
                    <TableRow 
                      key={contrato.id} 
                      className={`cursor-pointer hover:bg-slate-50 ${selectedContrato === contrato.id ? 'bg-blue-50' : ''}`}
                      onClick={() => fetchDREContrato(contrato.id)}
                    >
                      <TableCell>
                        <div>
                          <p className="font-medium text-luvcorp-navy">{contrato.nome}</p>
                          {contrato.cliente && (
                            <p className="text-xs text-slate-500">{contrato.cliente}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">{formatCurrency(contrato.valor_contrato || 0)}</TableCell>
                      <TableCell className="text-right text-green-600">{formatCurrency(contrato.receitas)}</TableCell>
                      <TableCell className="text-right text-red-600">{formatCurrency(contrato.despesas)}</TableCell>
                      <TableCell className={`text-right font-medium ${contrato.resultado >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(contrato.resultado)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge className={contrato.margem >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                          {formatPercent(contrato.margem)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            fetchDREContrato(contrato.id);
                          }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {contratos.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                        <FileBarChart className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                        Nenhum contrato/projeto encontrado
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Contract Detail */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileBarChart className="h-5 w-5 text-luvcorp-gold" />
                Detalhes do Contrato
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dreData ? (
                <div className="space-y-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h3 className="font-semibold text-luvcorp-navy">{dreData.contrato.nome}</h3>
                    {dreData.contrato.cliente && (
                      <p className="text-sm text-slate-500">{dreData.contrato.cliente}</p>
                    )}
                    <div className="mt-2 text-sm">
                      <span className="text-slate-500">Valor do Contrato: </span>
                      <span className="font-medium">{formatCurrency(dreData.contrato.valor_contrato || 0)}</span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-slate-600">Receitas</span>
                      <span className="font-medium text-green-600">{formatCurrency(dreData.receitas)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-slate-600">Despesas</span>
                      <span className="font-medium text-red-600">({formatCurrency(dreData.despesas)})</span>
                    </div>
                    <div className="flex justify-between py-3 bg-luvcorp-navy text-white px-3 rounded-lg">
                      <span className="font-bold">Resultado</span>
                      <span className="font-bold">{formatCurrency(dreData.resultado)}</span>
                    </div>
                    <div className="flex justify-center py-2">
                      <Badge className={`text-lg px-4 py-1 ${dreData.margem >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        Margem: {formatPercent(dreData.margem)}
                      </Badge>
                    </div>
                  </div>

                  {dreData.custos_por_centro?.length > 0 && (
                    <div className="pt-4 border-t">
                      <h4 className="font-medium text-sm text-slate-500 mb-2">Custos por Centro</h4>
                      <div className="space-y-2">
                        {dreData.custos_por_centro.map((cc, index) => (
                          <div key={index} className="flex justify-between text-sm">
                            <span className="text-slate-600">{cc.centro_nome}</span>
                            <span className="text-red-600">{formatCurrency(cc.valor)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-400">
                  <Eye className="h-12 w-12 mx-auto mb-4" />
                  <p>Selecione um contrato para ver detalhes</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
}
